import { useEffect, useState } from 'react'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import { authedFetch } from '../utils/shopifyFetch'
import { readSession } from '../utils/safeStorage'

// Detect iframe embedding — more reliable than checking ?host= which may not
// be present if Shopify loads the app without query params on some paths.
const EMBEDDED = window.self !== window.top ||
  !!new URLSearchParams(window.location.search).get('host') ||
  !!readSession('shopify_host')

// Matches the breakpoint in globals.css where the sidebar becomes a drawer.
const MOBILE_QUERY = '(max-width: 900px)'


/**
 * A permission the app needs and has not been granted.
 *
 * This lived on the Shopify connection card, which is a page most sellers
 * never open — so the one thing they had to act on was the one thing they
 * would not see. It belongs wherever they are.
 *
 * Shown only when something is genuinely missing, so a healthy install
 * never sees it, and dismissible for the session because a bar that cannot
 * be closed is worse than the problem on a screen someone is trying to use.
 */
/**
 * Shown once the seller is paying, and not before.
 *
 * Two conditions, and both matter. A permission has to be genuinely
 * missing — it always did — and the shop has to have an active
 * subscription.
 *
 * The second is new. A seller still deciding whether to buy the app does
 * not need a bar asking them to reconnect Shopify; it reads as a fault
 * with the thing they are evaluating, during the one session where that
 * impression is expensive. Once they have subscribed the calculation
 * inverts: the missing permission is now stopping work they are paying
 * for, and that is worth interrupting them about.
 *
 * It was briefly switched off altogether. That hid it from the sellers who
 * need it as well, so the gate is the subscription rather than a constant.
 */
function ScopeBanner({ shop }) {
  const [missing, setMissing] = useState([])
  const [paid,    setPaid]    = useState(false)
  const [hidden,  setHidden]  = useState(false)

  useEffect(() => {
    if (!shop) return
    let alive = true
    // Both, because the banner is the intersection: a missing permission
    // AND a paying seller. Failing either read leaves it hidden, which is
    // the safe way round — a banner shown in error is worse than one
    // missed, and Settings → Shopify reports the scope regardless.
    Promise.allSettled([
      authedFetch(`/api/settings?shop=${shop}`).then(r => r.json()),
      authedFetch(`/api/billing/status?shop=${shop}`).then(r => r.json()),
    ]).then(([settings, billing]) => {
      if (!alive) return
      if (settings.status === 'fulfilled') {
        setMissing(settings.value?.shopify?.missingScopes || [])
      }
      if (billing.status === 'fulfilled') {
        // An approved subscription, trial included: they have committed,
        // which is the line this is drawn at.
        setPaid(billing.value?.isActive === true)
      }
    })
    return () => { alive = false }
  }, [shop])

  if (hidden || !paid || !missing.length) return null

  return (
    <div role="status" style={{
      display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
      margin:'12px 24px 0', padding:'11px 14px',
      background:'var(--bg3)', border:'1px solid var(--amber, #c99a2e)',
      borderRadius:'var(--radius)',
    }}>
      <span style={{ fontSize:13, fontWeight:650 }}>Shopify needs one more permission</span>
      <span style={{ fontSize:12.5, color:'var(--text2)', flex:1, minWidth:240 }}>
        Products imported from eBay are created but stay off your storefront until this is
        granted. Reconnecting takes a few seconds and keeps your settings, listings and history.
      </span>
      {/* _top, because the app runs inside Shopify's iframe and Shopify's
          own OAuth screen refuses to be framed. Without it the browser
          blocks the navigation and the seller gets a broken page icon
          where the consent screen should be. */}
      <a className="btn btn-sm btn-primary" target="_top" rel="noopener"
         href={`/shopify/install?shop=${encodeURIComponent(shop || '')}`}>
        Reconnect Shopify
      </a>
      <button onClick={() => setHidden(true)} aria-label="Dismiss"
        style={{ border:'none', background:'none', cursor:'pointer',
                 color:'var(--text3)', fontSize:16, lineHeight:1, padding:'0 2px' }}>×</button>
    </div>
  )
}

export default function Layout({ children, addToast, shop }) {
  // When embedded in Shopify Admin (host param present), Shopify provides
  // the left sidebar navigation via NavigationMenu. Hide our custom sidebar.
  const embedded = EMBEDDED

  const [navOpen,  setNavOpen]  = useState(false)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  )

  // Track the breakpoint rather than reading innerWidth once, so rotating a
  // phone or resizing a window keeps the drawer state honest.
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const onChange = e => {
      setIsMobile(e.matches)
      if (!e.matches) setNavOpen(false)   // back to desktop: drawer is moot
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Escape closes the drawer, matching how the modals behave.
  useEffect(() => {
    if (!navOpen) return
    const onKey = e => { if (e.key === 'Escape') setNavOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navOpen])

  // Stop the page scrolling underneath the open drawer.
  useEffect(() => {
    if (!navOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [navOpen])

  const showDrawer = !embedded && isMobile

  return (
    <div className={embedded ? 'app app--embedded' : 'app'}>
      {!embedded && (
        <Sidebar
          shop={shop}
          open={navOpen}
          closed={showDrawer && !navOpen}
          onNavigate={() => setNavOpen(false)}
        />
      )}
      {showDrawer && navOpen && (
        <button
          type="button"
          className="nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      )}
      <div className="main">
        <Topbar
          addToast={addToast}
          shop={shop}
          showNavToggle={showDrawer}
          navOpen={navOpen}
          onToggleNav={() => setNavOpen(o => !o)}
        />
        <ScopeBanner shop={shop} />
        <main id="main-content" className="page-content">{children}</main>
      </div>
    </div>
  )
}
